'use client'

import { useCallback, useEffect, useRef } from 'react'
import {
  type CartItem,
  type CategoryDTO,
  type CheckoutProduct,
  type Currency,
  type Option
} from 'ecosystem'
import { getCartItemPrice, sumCart } from 'shared-utils'
import useGoogleAnalytics from './use-google-analytics'
import { type GaPurchaseOrder } from './types'

interface UseViewCartProps {
  categories: Option<CategoryDTO[]>
  cartItems: CartItem[]
  cartTotal: number
  currency: Currency
  registerViewCartEvent?: boolean
}

export const useAnalyticsCart = ({
  categories,
  cartTotal,
  cartItems,
  currency,
  registerViewCartEvent
}: UseViewCartProps) => {
  const GA = useGoogleAnalytics()
  const isViewCartEventTracked = useRef(false)

  const viewCartEvent = useCallback(
    () => GA.viewCart(cartItems, cartTotal, categories, currency),
    [GA, cartItems, cartTotal, categories, currency]
  )

  useEffect(() => {
    if (!isViewCartEventTracked.current && registerViewCartEvent) {
      isViewCartEventTracked.current = viewCartEvent()
    }
  }, [registerViewCartEvent, viewCartEvent])

  const addToCartEvent = useCallback(
    (cartItem: CartItem) => {
      const cartWithoutItem = cartItems.filter((item) => item.id !== cartItem.id)
      const qty = parseInt(String(cartItem.qty || 1))
      const itemPrice = getCartItemPrice(cartItem)
      const newCartTotal = sumCart(cartWithoutItem) + itemPrice * Number(qty)
      return GA.updateCart(
        'add',
        newCartTotal,
        [
          {
            ...cartItem,
            qty
          }
        ],
        categories,
        currency
      )
    },
    [GA, cartItems, categories, currency]
  )

  const removeFromCartEvent = useCallback(
    (cartItem: CartItem) => {
      const cartWithoutItem = cartItems.filter((item) => item.id !== cartItem.id)
      const newCartTotal = sumCart(cartWithoutItem)

      return GA.updateCart('remove', newCartTotal, [cartItem], categories, currency)
    },
    [GA, cartItems, categories, currency]
  )

  const checkoutEvent = useCallback(
    () => GA.checkout(cartItems, cartTotal, categories, currency),
    [GA, cartItems, cartTotal, categories, currency]
  )

  const purchaseEvent = useCallback(
    (order: GaPurchaseOrder, items: CheckoutProduct[]) =>
      GA.purchase(order, items, categories, currency),
    [GA, categories, currency]
  )

  return {
    checkoutEvent,
    purchaseEvent,
    addToCartEvent,
    removeFromCartEvent,
    viewCartEvent
  }
}
